/**
 * useStaffInvitations — TanStack Query hook over
 * `GET /admin/staff/invitations`.
 *
 * The list of OPEN platform invitations (not accepted, not revoked),
 * powering the "Pending invitations" section on the `/staff` list page.
 * Available to root + support (read). Response envelope:
 * `{ invitations: PlatformInvitationItemDto[] }`.
 *
 * Each row carries a server-DERIVED `status`:
 *   - 'invited' — still pending (now <= expiresAt).
 *   - 'expired' — past its expiry (resend re-arms it).
 *
 * Status surface (closed discriminated union):
 *   - 'loading'   — request in flight.
 *   - 'ready'     — 200 OK; `invitations` populated (projected view-models).
 *   - 'forbidden' — 403; the caller is not platform staff. Distinct branch
 *                   so the page renders a permission-denied panel rather
 *                   than the generic Retry affordance.
 *   - 'error'     — anything else (5xx, network, schema drift).
 *
 * Query key: `['admin','staff','invitations']` — the canonical
 * invitations key that `useResendInvitation` / `useRevokeInvitation`
 * invalidate.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { isPlatformRole } from '../../../shared/auth/admin-permissions.js';
import type { PlatformRole } from '../../../shared/auth/admin-permissions.js';

/** Canonical invitations query key. Resend/revoke mutations invalidate this. */
export const STAFF_INVITATIONS_QUERY_KEY = [
  'admin',
  'staff',
  'invitations',
] as const;

/**
 * Pending-invitation row view-model — projection of
 * `PlatformInvitationItemDto` from `@artemis/contracts`.
 *
 * SEAM RULE: every field sources from a single DTO field; `role` is
 * runtime-guarded against contract drift (least-privileged fallback).
 */
export interface StaffInvitation {
  /** Source: `PlatformInvitationItemDto.id`. Invitation UUID. */
  readonly id: string;
  /** Source: `PlatformInvitationItemDto.email`. Invited email address. */
  readonly email: string;
  /** Source: `PlatformInvitationItemDto.role`. `'root' | 'support'`. */
  readonly role: PlatformRole;
  /** Source: `PlatformInvitationItemDto.status`. Server-derived. */
  readonly status: 'invited' | 'expired';
  /** Source: `PlatformInvitationItemDto.invitedAt`. ISO-8601. */
  readonly invitedAt: string;
  /** Source: `PlatformInvitationItemDto.expiresAt`. ISO-8601. */
  readonly expiresAt: string;
}

export type UseStaffInvitationsResult =
  | { readonly status: 'loading'; readonly refetch: () => void }
  | {
      readonly status: 'ready';
      readonly invitations: readonly StaffInvitation[];
      readonly refetch: () => void;
    }
  | { readonly status: 'forbidden'; readonly refetch: () => void }
  | { readonly status: 'error'; readonly refetch: () => void };

/**
 * Sentinel distinguishing a 403 from any other error inside the
 * queryFn — we cannot `throw` and rely on TanStack's error path because
 * the page needs a permission-denied branch that is NOT the retry/error
 * affordance.
 */
const FORBIDDEN = Symbol('staff-invitations:forbidden');

interface InvitationsEnvelope {
  readonly invitations: readonly InvitationDto[];
}

interface InvitationDto {
  readonly id: string;
  readonly email: string;
  readonly role: string;
  readonly status: 'invited' | 'expired';
  readonly invitedAt: string;
  readonly expiresAt: string;
}

/** Project a wire DTO into the UI view-model, guarding `role` on drift. */
export function projectStaffInvitation(dto: InvitationDto): StaffInvitation {
  return {
    id: dto.id,
    email: dto.email,
    // `role` is `'root' | 'support'` per the contract; guard at runtime
    // and fall back to 'support' (least-privileged) on drift.
    role: isPlatformRole(dto.role) ? dto.role : 'support',
    status: dto.status,
    invitedAt: dto.invitedAt,
    expiresAt: dto.expiresAt,
  };
}

export function useStaffInvitations(): UseStaffInvitationsResult {
  const query = useQuery({
    queryKey: STAFF_INVITATIONS_QUERY_KEY,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET('/admin/staff/invitations');
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /admin/staff/invitations failed with status ${result.response.status}`,
        );
      }
      return result.data as InvitationsEnvelope;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  return {
    status: 'ready',
    invitations: query.data.invitations.map(projectStaffInvitation),
    refetch,
  };
}
